import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { AgentThreadToolUseContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  displayToolName,
  isClientToolName,
} from "@/shared/domain/agentThread/clientTools/ClientTools";
import { describeToolCall } from "@/shared/domain/agentThread/describeToolCall";
import { withoutServerInjectedFields } from "@/shared/domain/agentThread/toolInput";
import { BuildIcon, LaptopMacIcon } from "@/shared/foundation/icons/mui";
import { Collapsible } from "@/shared/foundation/primitives";

import { type ToolCallStatus } from "../liveToolCalls";

import styles from "./AssistantToolUseContent.module.css";
import { RunPythonCard } from "./RunPythonCard";
import { ToolCallStatusBadge } from "./ToolCallStatusBadge";
import { ToolCallInput } from "./ToolDetailPanel";

interface AssistantToolUseContentProps {
  toolUse: AgentThreadToolUseContent;
  status: ToolCallStatus;
  /** The thread's activity, shown as a running call's status (see `ToolCallStatusBadge`). */
  activity?: string | null;
}

/**
 * A tool call from the moment the model makes it: the tool's pill, its status,
 * and its arguments, open by default so a long-running call shows what it runs.
 */
const AssistantToolUseContent = ({
  toolUse,
  status,
  activity,
}: AssistantToolUseContentProps) => {
  const isClient = isClientToolName(toolUse.tool_name);
  const Icon = isClient ? LaptopMacIcon : BuildIcon;
  const verb = status === "pending" || status === "running" ? "Using" : "Used";
  const input = withoutServerInjectedFields(toolUse.input);
  const hasArguments = Object.keys(input).length > 0;

  // A live run_python call shows as its card, so it reads the same before and after it runs.
  if (
    toolUse.tool_name === "run_python" &&
    (status === "pending" || status === "running")
  ) {
    return (
      <div className={styles.indentedContainer}>
        <div className={styles.call}>
          <div className={styles.messageContainer}>
            <div className={styles.textAndIconContainer}>
              <Icon className={styles.buildIcon} />
              <RobotoTypography className={styles.text}>
                <b>Running Python…</b>
              </RobotoTypography>
            </div>
          </div>
          <RunPythonCard
            toolUseId={toolUse.tool_use_id}
            input={input}
            state={{ kind: "live", status, activity }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.indentedContainer}>
      <div className={styles.call}>
        <div className={styles.messageContainer}>
          <div className={styles.textAndIconContainer}>
            <Icon
              className={isClient ? styles.clientToolIcon : styles.buildIcon}
            />
            <RobotoTypography className={styles.text}>
              {verb} {isClient ? "Client " : ""}Tool{" "}
              <b>
                {describeToolCall(
                  displayToolName(toolUse.tool_name),
                  toolUse.input,
                )}
              </b>
            </RobotoTypography>
          </div>
          <ToolCallStatusBadge status={status} activity={activity} />
        </div>
        {hasArguments && (
          <Collapsible.Root defaultOpen className={styles.arguments}>
            <Collapsible.Trigger className={styles.argumentsTrigger}>
              Arguments
            </Collapsible.Trigger>
            <Collapsible.Panel className={styles.argumentsPanel}>
              <ToolCallInput toolName={toolUse.tool_name} input={input} />
            </Collapsible.Panel>
          </Collapsible.Root>
        )}
      </div>
    </div>
  );
};

export { AssistantToolUseContent };
